"use client"

import React, { useState, useRef, useEffect } from "react"
import "./styles.css"

import { acceptanceHead, bargainingHead, denialHead, depressionHead, angerHead } from "assets"

export default function StagesOfGrief() {
  const [currentStage, setCurrentStage] = useState("denial")
  const [isDragging, setIsDragging] = useState(false)
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 })
  const containerRef = useRef(null)
  const draggableRef = useRef(null)

  const stages = [
    { id: "denial", label: "I'm OK <br/> I'm Fine <br/> Gwenchana" },
    { id: "anger", label: "<span style='color: #FF3131'>WHY ME!?</span>" },
    { id: "bargaining", label: "<span style='font-style: italic'>thinking to turn back time</span>" },
    { id: "depression", label: "Leave me alone" },
    { id: "acceptance", label: "<b>Embrace & Elevate</b>" },
  ]

  // Store the original scroll position
  const scrollPosRef = useRef({ x: 0, y: 0 })

  // Handle mouse down / touch start
  const handleDragStart = (e) => {
    // Prevent default but don't stop propagation
    if (e.cancelable) {
      e.preventDefault()
    }

    // Get cursor position
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY

    // Set initial position
    setDragPosition({ x: clientX, y: clientY })
    setIsDragging(true)

    // Store current scroll position
    scrollPosRef.current = {
      x: window.scrollX,
      y: window.scrollY,
    }
  }

  // Handle mouse move / touch move
  const handleDrag = (e) => {
    if (!isDragging) return

    // Prevent default but don't stop propagation
    if (e.cancelable) {
      e.preventDefault()
    }

    // Get current cursor position
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY

    // Update position
    setDragPosition({ x: clientX, y: clientY })

    // Maintain the scroll position
    window.scrollTo(scrollPosRef.current.x, scrollPosRef.current.y)
  }

  // Handle mouse up / touch end
  const handleDragEnd = (e) => {
    if (!isDragging) return

    // Get final position
    const finalX = dragPosition.x
    const finalY = dragPosition.y

    // Find which stage the element was dropped on
    const stageElements = document.querySelectorAll(".mobile-stage")
    let targetStage = null

    stageElements.forEach((stageEl) => {
      const rect = stageEl.getBoundingClientRect()

      // Check if the final position is within this stage's bounds
      if (finalX >= rect.left && finalX <= rect.right && finalY >= rect.top && finalY <= rect.bottom) {
        targetStage = stageEl.getAttribute("data-stage")
      }
    })

    // If we found a valid target stage, update the current stage
    if (targetStage) {
      setCurrentStage(targetStage)
    }

    setIsDragging(false)
  }

  // Set up event listeners
  useEffect(() => {
    const handleMouseMove = (e) => handleDrag(e)
    const handleTouchMove = (e) => handleDrag(e)
    const handleMouseUp = (e) => handleDragEnd(e)
    const handleTouchEnd = (e) => handleDragEnd(e)

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove, { passive: false })
      document.addEventListener("touchmove", handleTouchMove, { passive: false })
      document.addEventListener("mouseup", handleMouseUp)
      document.addEventListener("touchend", handleTouchEnd)

      // Targeted scroll prevention only for the draggable area
      const preventScrollInDraggableArea = (e) => {
        // Only prevent default if the touch is in or near the draggable area
        if (containerRef.current && e.cancelable) {
          const containerRect = containerRef.current.getBoundingClientRect()
          const touchX = e.touches[0].clientX
          const touchY = e.touches[0].clientY

          // Add some padding around the container for better user experience
          const padding = 50

          if (
            touchX >= containerRect.left - padding &&
            touchX <= containerRect.right + padding &&
            touchY >= containerRect.top - padding &&
            touchY <= containerRect.bottom + padding
          ) {
            e.preventDefault()

            // Maintain the scroll position
            window.scrollTo(scrollPosRef.current.x, scrollPosRef.current.y)
          }
        }
      }

      document.addEventListener("touchmove", preventScrollInDraggableArea, { passive: false })

      return () => {
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("touchmove", handleTouchMove)
        document.removeEventListener("mouseup", handleMouseUp)
        document.removeEventListener("touchend", handleTouchEnd)
        document.removeEventListener("touchmove", preventScrollInDraggableArea)
      }
    }

    return () => {}
  }, [isDragging, dragPosition])

  // Handle stage click directly (alternative to drag and drop)
  const handleStageClick = (stageId) => {
    if (!isDragging) {
      setCurrentStage(stageId)
    }
  }

  return (
    <div className="mobile-grief-container">
      <div className="mobile-stages-wrapper" ref={containerRef}>
        <div className="mobile-stages-grid">
          {stages.map((stage, index) => (
            <React.Fragment key={stage.id}>
              <div
                className={`mobile-stage ${currentStage === stage.id ? "mobile-active" : ""}`}
                data-stage={stage.id}
                onClick={() => handleStageClick(stage.id)}
              >
                <div className="mobile-stage-id">
                  {index + 1}. {stage.id}
                </div>

                {currentStage === stage.id && !isDragging && (
                  <div
                    ref={draggableRef}
                    className="mobile-draggable"
                    onMouseDown={handleDragStart}
                    onTouchStart={handleDragStart}
                  >
                    <img
                      src={
                        currentStage === "denial"
                          ? denialHead
                          : currentStage === "anger"
                            ? angerHead
                            : currentStage === "bargaining"
                              ? bargainingHead
                              : currentStage === "depression"
                                ? depressionHead
                                : acceptanceHead
                      }
                      alt={stage.id}
                    />
                  </div>
                )}
              </div>

              {/* {index < stages.length - 1 && (
                <div className="mobile-dotted-line"></div>
              )} */}
            </React.Fragment>
          ))}

          {isDragging && (
            <div
              className="mobile-draggable mobile-dragging"
              style={{
                position: "fixed", // Use fixed positioning for more accurate tracking
                left: `${dragPosition.x}px`,
                top: `${dragPosition.y}px`,
                transform: "translate(-50%, -50%)", // Center the element on the cursor
                pointerEvents: "none", // Prevent the element from interfering with mouse events
                zIndex: 1000,
              }}
            >
              <img
                src={
                  currentStage === "denial"
                    ? denialHead
                    : currentStage === "anger"
                      ? angerHead
                      : currentStage === "bargaining"
                        ? bargainingHead
                        : currentStage === "depression"
                          ? depressionHead
                          : acceptanceHead
                }
                alt={currentStage || ""}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
